/**
 * React Query hooks for the owner self-service sub-delegate management
 * page — Feature 075 (plan 013, Workstream C).
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { subDelegateManagementApi } from '../api/subDelegateManagementApi';
import type {
  InviteDelegatePayload,
  InviteDelegateResponse,
  RevokeDelegateResponse,
  SubDelegateList,
  UpdateDelegatePermissionsPayload,
  UpdateDelegatePermissionsResponse,
} from '../types/subDelegateManagement';

export const ownerSubDelegateKeys = {
  all: ['workspace', 'sub-delegates'] as const,
};

const useInvalidateList = () => {
  const queryClient = useQueryClient();
  return () =>
    queryClient.invalidateQueries({ queryKey: ownerSubDelegateKeys.all });
};

export function useOwnerSubDelegates(): UseQueryResult<SubDelegateList, Error> {
  return useQuery({
    queryKey: ownerSubDelegateKeys.all,
    queryFn: () => subDelegateManagementApi.listSubDelegates(),
    staleTime: 30_000,
  });
}

export function useInviteSubDelegate(): UseMutationResult<
  InviteDelegateResponse,
  Error,
  InviteDelegatePayload
> {
  const invalidate = useInvalidateList();
  return useMutation({
    mutationFn: (payload) => subDelegateManagementApi.inviteSubDelegate(payload),
    onSuccess: invalidate,
  });
}

export function useResendSubDelegateInvite(): UseMutationResult<
  InviteDelegateResponse,
  Error,
  string
> {
  const invalidate = useInvalidateList();
  return useMutation({
    mutationFn: (pendingId) =>
      subDelegateManagementApi.resendSubDelegateInvite(pendingId),
    onSuccess: invalidate,
  });
}

export function useCancelSubDelegateInvite(): UseMutationResult<void, Error, string> {
  const invalidate = useInvalidateList();
  return useMutation({
    mutationFn: (pendingId) =>
      subDelegateManagementApi.cancelSubDelegateInvite(pendingId),
    onSuccess: invalidate,
  });
}

export function useRevokeSubDelegate(): UseMutationResult<
  RevokeDelegateResponse,
  Error,
  string
> {
  const invalidate = useInvalidateList();
  return useMutation({
    mutationFn: (relationshipId) =>
      subDelegateManagementApi.revokeSubDelegate(relationshipId),
    onSuccess: invalidate,
  });
}

export function useUpdateSubDelegatePermissions(): UseMutationResult<
  UpdateDelegatePermissionsResponse,
  Error,
  UpdateDelegatePermissionsPayload
> {
  const invalidate = useInvalidateList();
  return useMutation({
    mutationFn: ({ relationshipId, permissions }) =>
      subDelegateManagementApi.updateSubDelegatePermissions(
        relationshipId,
        permissions,
      ),
    onSuccess: invalidate,
  });
}
